import { useState, useEffect } from 'react'
import {
    Grid,
    Typography,
    Button,
    Box
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress } from '@material-ui/core'
import CancelIcon from 'mdi-react/CancelCircleOutlineIcon'
import StartAuthTrigger from '../InstantRentApplication/StartAuthTrigger'
import OutlineLockPersonIcon from '../../images/ic_outline-lock-person.svg'
import Api from '../../services/applications'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
    formContainer: {
        width: '70%',
        margin: '3rem auto',
        padding: '2rem',
        border: '1px solid #ddd',
        borderRadius: '10px',
        backgroundColor: '#fff',
        [theme.breakpoints.down('sm')]: {
            width: '90%'
        }
    },
    header: {
        fontSize: '1.8rem',
        fontWeight: 600,
        marginBottom: '1.5rem',
        textAlign: 'center'
    },
    description: {
        marginBottom: '1.5rem',
        lineHeight: 1.6,
        textAlign: 'center'
    },
    checkboxRow: {
        marginBottom: '1rem'
    },
    errorText: {
        color: 'red',
        marginBottom: '1rem',
        textAlign: 'center'
    },
    authButtonWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '2rem'
    },
    authPrompt: {
        padding: '2rem 0'
    },
    centeredText: {
        textAlign: 'center'
    },
    boldText: {
        fontWeight: 'bold'
    },
    alertBox: {
        marginTop: '2rem',
        padding: '2rem',
        border: '1px solid #D43838',
        borderRadius: '12px',
        textAlign: 'center'
    },
    icon: {
        marginBottom: '1rem'
    },
    spinnerWrap: {
        flex: '0 0 24px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}))

const LandlordIdentifier = () => {
    type RouteParams = {
        userId: string
    }

    const classes = useStyles()
    const { userId } = useParams<RouteParams>()
    const [isEmpty, setIsEmpty] = useState(false)
    const [authError, setAuthError] = useState(false)
    const [authLoading, setAuthLoading] = useState(false)
    const { t } = useTranslation()
    const history = useHistory()
    // ✅ grab from redux

    const collateralVerified = useSelector(
        (state: any) => state.users?.users?.[userId]?.collateralVerified
    )

    useEffect(() => {
        if (collateralVerified === true) {
            history.push('/apartments')
        }
    }, [collateralVerified, history])

    const startCreditScoreAuth = () => {
        setIsEmpty(false)
        setAuthLoading(true)

        Api.initiateAuthorizationForCreditScore({ userId })
            .then((response) => {
                if (response?.authenticationUrl) {
                    window.location.href = response.authenticationUrl
                } else {
                    setAuthError(true)
                    setAuthLoading(false)
                }
            })
            .catch(() => {
                setAuthError(true)
                setAuthLoading(false)
            })
    }

    return (
        <Grid container className={classes.formContainer} direction='column'>
            <Typography className={classes.header}>
                {t('landlordIdentification.header')}
            </Typography>

            <Typography className={classes.description}>
                {t('landlordIdentification.description')}
            </Typography>

            {isEmpty && (
                <Typography className={classes.errorText}>
                    {t('landlordIdentification.mustAccept')}
                </Typography>
            )}

            <div className={classes.authButtonWrapper}>
                <StartAuthTrigger
                    suggestContainerStyle={classes.authPrompt}
                    hideSuggestIcon
                    renderText={() => (
                        <Grid container justifyContent='center' alignItems='center'>
                            <Grid item style={{ marginRight: 12 }}>
                                <img src={OutlineLockPersonIcon} alt={t('landlordIdentification.lockIconAlt')} />
                            </Grid>
                            <Grid item>
                                <Typography
                                    variant='h6'
                                    className={`${classes.centeredText} ${classes.boldText}`}
                                >
                                    {t('landlordIdentification.startAuth')}
                                </Typography>
                            </Grid>
                            {authLoading && (
                                <Grid item className={classes.spinnerWrap} style={{ marginLeft: 12 }}>
                                    <CircularProgress size={24} style={{ color: '#94004A' }} />
                                </Grid>
                            )}
                        </Grid>
                    )}
                    isModal
                    customSuggestTextStyles={`${classes.centeredText} ${classes.boldText}`}
                    doNextStep={startCreditScoreAuth}
                    submit
                    setIsEmpty={setIsEmpty}
                />
            </div>

            {authError && (
                <Box className={classes.alertBox}>
                    <CancelIcon color='#D43838' size={48} className={classes.icon} />
                    <Typography variant='h6'>
                        {t('landlordIdentification.authFailed')}
                    </Typography>
                    <Button
                        style={{ marginTop: '1rem' }}
                        variant='contained'
                        color='primary'
                        onClick={() => setAuthError(false)}
                    >
                        {t('landlordIdentification.tryAgain')}
                    </Button>
                </Box>
            )}
        </Grid>
    )
}

export default LandlordIdentifier
